package com.sddamico.draftmachine.ds.overlays

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmMotion
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayGrid
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.motion.LocalExiting
import com.sddamico.draftmachine.ds.motion.Presence
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.shadowPop
import com.sddamico.draftmachine.ds.textureBrushed
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/** sm 400 / md 520 / lg 680 — the dialog's max content width. */
enum class ModalSize { Sm, Md, Lg }

/**
 * A riveted steel plate lowered over the machine — for focused tasks that pause everything behind
 * them. Copper seam on top; scrim click + Escape + header X all dismiss (only when [onClose] is set).
 * Renders nothing while [open] is false; closing plays a short exit first. Port of
 * `components/overlays/Modal.jsx`.
 *
 * @param modifier applied to the dialog panel (mirrors the JSX `style` spread).
 * @param onClose omit to force the caller to resolve the modal through the [footer] instead.
 * @param footer a right-aligned action row (e.g. Buttons).
 * @param content the dialog body (scrolls when it overflows 86vh).
 */
@Composable
fun Modal(
  open: Boolean,
  modifier: Modifier = Modifier,
  title: String? = null,
  icon: String? = null,
  size: ModalSize = ModalSize.Md,
  onClose: (() -> Unit)? = null,
  footer: (@Composable () -> Unit)? = null,
  content: @Composable () -> Unit,
) {
  Presence(open) { ModalPanel(modifier, title, icon, size, onClose, footer, content) }
}

@Composable
private fun ModalPanel(
  modifier: Modifier,
  title: String?,
  icon: String?,
  size: ModalSize,
  onClose: (() -> Unit)?,
  footer: (@Composable () -> Unit)?,
  content: @Composable () -> Unit,
) {
  val exiting = LocalExiting.current
  val exitMs = "${DmMotion.overlayExitMs}ms"
  if (onClose != null && !exiting) {
    DisposableEffect(onClose) {
      val onKey: (Event) -> Unit = { e ->
        if ((e as? KeyboardEvent)?.key == "Escape") onClose()
      }
      document.addEventListener("keydown", onKey)
      onDispose { document.removeEventListener("keydown", onKey) }
    }
  }

  val maxWidth = when (size) {
    ModalSize.Sm -> 400.px
    ModalSize.Md -> 520.px
    ModalSize.Lg -> 680.px
  }

  Div(
    attrs = Modifier
      .displayGrid()
      .padding(DmSpacing.space6)
      .styleModifier {
        property("position", "fixed")
        property("inset", "0")
        property("z-index", "100")
        property("place-items", "center")
        property("background", "rgba(13,16,19,.66)")
        property("backdrop-filter", "blur(3px)")
        property("-webkit-backdrop-filter", "blur(3px)")
        property(
          "animation",
          if (exiting) "dm-fade-out $exitMs ${DmMotion.easeOut} both"
          else "dm-fade-in ${DmMotion.durBase} ${DmMotion.easeOut} both",
        )
        if (exiting) property("pointer-events", "none")
      }
      .toAttrs { if (onClose != null) onClick { onClose() } }
  ) {
    Div(
      attrs = Modifier
        .fillMaxWidth()
        .maxWidth(maxWidth)
        .displayFlex()
        .flexDirection(FlexDirection.Column)
        .backgroundColor(DmColor.surfacePanel)
        .textureBrushed()
        .border(1.px, LineStyle.Solid, DmColor.borderStrong)
        .borderTop(DmEffects.borderPlate, LineStyle.Solid, DmColor.brass600)
        .borderRadius(DmEffects.radiusLg)
        .shadowPop()
        .styleModifier {
          property("max-height", "86vh")
          property(
            "animation",
            if (exiting) "dm-dialog-out $exitMs ${DmMotion.easeExit} both"
            else "dm-dialog-in ${DmMotion.pageEnterMs}ms ${DmMotion.easeSnap} both",
          )
        }
        .then(modifier)
        .toAttrs {
          attr("role", "dialog")
          attr("aria-modal", "true")
          onClick { it.stopPropagation() }
        }
    ) {
      if (title != null || onClose != null) {
        Div(
          attrs = Modifier
            .displayFlex()
            .alignItems(AlignItems.Center)
            .gap(DmSpacing.space3)
            .padding(topBottom = DmSpacing.space4, leftRight = DmSpacing.space5)
            .borderBottom(1.px, LineStyle.Solid, DmColor.border)
            .toAttrs()
        ) {
          if (icon != null) {
            Icon(icon, IconWeight.Bold, Modifier.fontSize(20.px).color(DmColor.brass400).flex("0 0 auto"))
          }
          Div(attrs = Modifier.flex("1").roleH3().toAttrs()) {
            if (title != null) Text(title)
          }
          if (onClose != null) {
            DomButton(
              attrs = Modifier
                .displayInlineFlex()
                .color(DmColor.textMuted)
                .cursor(Cursor.Pointer)
                .padding(4.px)
                .styleModifier { property("background", "none"); property("border", "none") }
                .toAttrs { attr("aria-label", "Close"); onClick { onClose() } }
            ) {
              Icon("x", IconWeight.Bold, Modifier.fontSize(18.px))
            }
          }
        }
      }
      Div(
        attrs = Modifier
          .padding(DmSpacing.space5)
          .styleModifier { property("overflow-y", "auto") }
          .toAttrs()
      ) {
        content()
      }
      if (footer != null) {
        Div(
          attrs = Modifier
            .displayFlex()
            .justifyContent(JustifyContent.FlexEnd)
            .gap(DmSpacing.space3)
            .padding(topBottom = DmSpacing.space4, leftRight = DmSpacing.space5)
            .borderTop(1.px, LineStyle.Solid, DmColor.border)
            .toAttrs()
        ) {
          footer()
        }
      }
    }
  }
}
